import { NavLink, useParams } from "react-router-dom";
import useLabels from "../../hooks/useLabels";
import SecondaryBar from "../SecondaryBar";
import { CTA, InfoCard } from "../Generic";
// import ProtocolList from "../ProtocolList";
// import SubstanceList from "../SubstanceList";
import {
  selectDrugs,
  selectExams,
  selectTreatments,
  selectAnalysis,
  selectWineMaking,
  selectUsers,
  selectConfigs,
  selectAppTemplates,
} from "../../context/selectors";
import { getDrugs, getExams, getProtocols, getUsers, getWineMakingProtocols } from "../../services/utils";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { lazy, Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setDrugs } from "../../context/substances/drugsSlice";
import { setExams } from "../../context/substances/examsSlice";
import { setAnalysis } from "../../context/protocols/analysisSlice";
import { setTreatments } from "../../context/protocols/treatmentsSlice";
import { ROLES } from "../../constants/base";
import useAuth from "../../hooks/useAuth";
import grape from "../../assets/icons/grape.png";
import must from "../../assets/icons/must.png";
import wine from "../../assets/icons/wine.png";
import { SkeletonTableEdit } from "../Skeleton/Skeleton";
import { setWineMaking } from "../../context/protocols/wineMakingSlice";
import { setAppTemplates } from "../../context/protocols/appTemplateSlice";
import TabSelector from "../TabSelector";
import { getItem, setItem } from "../../services/LocalStorage";
import { setUsers } from "../../context/users/usersSlice";
import { Icon } from "../../components/Icon";

const ProtocolList = lazy(() => import("../ProtocolList"));
const WineMakingProtocolList = lazy(() => import("../WineMakingProtocolList"));
const ApplicationTemplateList = lazy(() => import("../ApplicationTemplateList"));
// const SubstanceList = lazy(() => import("../SubstanceList"));

const Protocols = ({ isMobile }) => {
  const [getLabel] = useLabels();
  const users = useSelector(selectUsers);
  const analysisProtocols = useSelector(selectAnalysis);
  const treatmentProtocols = useSelector(selectTreatments);
  const winemakingProtocols = useSelector(selectWineMaking);
  const appTemplates = useSelector(selectAppTemplates);
  const drugs = useSelector(selectDrugs);
  const exams = useSelector(selectExams);
  const configs = useSelector(selectConfigs);
  const [protocolsLen, setProtocolsLen] = useState({});

  const capitalize = (s) =>
    typeof s === 'string' && s.length > 0
      ? s.charAt(0).toUpperCase() + s.slice(1)
      : s;
  const tabs = [
    { name: capitalize(getLabel("analysis")), anchor: "analysis" },
    { name: capitalize(getLabel("treatment")), anchor: "treatment" },
    { name: capitalize(getLabel("physical")), anchor: "physical" },
    { name: capitalize(getLabel("cipWinemaking")), anchor: "winemaking-cip" },
    { name: capitalize(getLabel("winemaking")), anchor: "winemaking-pre" },
    { name: capitalize(getLabel("postWinemaking")), anchor: "winemaking-post" },
    { name: capitalize(getLabel("sparklingWinemaking")), anchor: "winemaking-sparkling" },
    { name: capitalize(getLabel("appTemplateTab")), anchor: "app-templates" },
  ];


  const getInitialTab = () => {
    try {
      const savedTab = getItem(`activeTab-protocols`);
      if (savedTab && tabs.some(tab => tab.anchor === savedTab)) {
        return savedTab;
      }
    } catch (error) {
      // ignore
    }
    return "analysis";
  };

  const [activeTab, setActiveTab] = useState(getInitialTab());

  const handleTabChange = (anchor) => {
    setActiveTab(anchor);
    setItem(`activeTab-protocols`, anchor);
  };

  const axiosPrivate = useAxiosPrivate();
  const dispatch = useDispatch();
  const { auth } = useAuth();
  const role = auth?.role || ROLES.GUEST;

  const loadAnalysisProtocols = async () => {
    const currentProtocols = (await getProtocols("analysis", axiosPrivate)) || [];
    dispatch(setAnalysis(currentProtocols));
  };

  const loadTreatmentProtocols = async () => {
    const currentProtocols = (await getProtocols("treatment", axiosPrivate)) || [];
    dispatch(setTreatments(currentProtocols));
  };

  const loadWinemakingProtocols = async () => {
    const loadedProtocols = await getProtocols("winemaking", axiosPrivate);
    dispatch(setWineMaking(loadedProtocols));
  };

  const loadAppTemplates = async () => {
    const data = await getWineMakingProtocols(axiosPrivate);
    dispatch(setAppTemplates(data || []));
  };

  const loadDrugs = async () => {
    const currentDrugs = await getDrugs(axiosPrivate);
    dispatch(setDrugs(currentDrugs));
  };

  const loadExams = async () => {
    const currentExams = await getExams(axiosPrivate);
    dispatch(setExams(currentExams));
  };

  const loadUsers = async () => {
    const currentUsers = await getUsers(axiosPrivate);
    dispatch(setUsers(currentUsers));
  };

  // Load users on mount
  useEffect(() => {
    if (!users || users.length === 0) {
      loadUsers();
    }
  }, []);

  // Load data when tab changes
  useEffect(() => {
    const loadDataForTab = async () => {
      if (activeTab === 'analysis') {
        if (!analysisProtocols || analysisProtocols.length === 0) {
          await loadAnalysisProtocols();
        }
        if (!exams || exams.length === 0) {
          await loadExams();
        }
      } else if (activeTab === 'treatment') {
        if (!treatmentProtocols || treatmentProtocols.length === 0) {
          await loadTreatmentProtocols();
        }
        if (!drugs || drugs.length === 0) {
          await loadDrugs();
        }
      } else if (activeTab?.startsWith('winemaking') || activeTab === 'physical') {
        if (!winemakingProtocols || winemakingProtocols.length === 0) {
          await loadWinemakingProtocols();
        }
      } else if (activeTab === 'app-templates') {
        if (!appTemplates || appTemplates.length === 0) {
          await loadAppTemplates();
        }
        // also load sub-protocol data for the form (analysis, treatment, winemaking)
        if (!analysisProtocols || analysisProtocols.length === 0) loadAnalysisProtocols();
        if (!treatmentProtocols || treatmentProtocols.length === 0) loadTreatmentProtocols();
        if (!winemakingProtocols || winemakingProtocols.length === 0) loadWinemakingProtocols();
      }
    };

    loadDataForTab();
  }, [activeTab]);

  const renderHeader = () => {
    return (
      <div className="primary_container_menu">
        <h2>{getLabel("protocols")}</h2>
      </div>
    );
  };

  useEffect(() => {
    // Calculate protocol lengths based on active tab
    let currentProtocols = [];
    if (activeTab === 'analysis') {
      currentProtocols = analysisProtocols || [];
    } else if (activeTab === 'treatment') {
      currentProtocols = treatmentProtocols || [];
    }

    if (!currentProtocols || currentProtocols?.length === 0) {
      setProtocolsLen({});
      return;
    }

    const tmp = {};
    tmp.tot = currentProtocols?.length || 0;
    const wine = currentProtocols?.filter((t) => t?.state === "WINE");
    const must = currentProtocols?.filter((t) => t?.state === "MUST");
    const grape = currentProtocols?.filter((t) => t?.state === "GRAPE");

    tmp.wine = wine?.length;
    tmp.must = must?.length;
    tmp.grape = grape?.length;

    tmp.wines = {
      red: wine?.filter((t) => t?.color === "RED")?.length,
      white: wine?.filter((t) => t?.color === "WHITE")?.length,
      rose: wine?.filter((t) => t?.color === "ROSE")?.length,
      orange: wine?.filter((t) => t?.color === "ORANGE")?.length,
    };
    tmp.musts = {
      red: must?.filter((t) => t?.color === "RED")?.length,
      white: must?.filter((t) => t?.color === "WHITE")?.length,
      rose: must?.filter((t) => t?.color === "ROSE")?.length,
      orange: must?.filter((t) => t?.color === "ORANGE")?.length,
    };
    tmp.grapes = {
      red: grape?.filter((t) => t?.color === "RED")?.length,
      white: grape?.filter((t) => t?.color === "WHITE")?.length,
      rose: grape?.filter((t) => t?.color === "ROSE")?.length,
    };

    setProtocolsLen(tmp);
  }, [activeTab, analysisProtocols, treatmentProtocols]);

  const getTooltipContent = (obj = {}) => {
    return (
      <ul>
        {Object.keys(obj)?.map((o, index) => (
          <li className="tooltip_label" key={`tooltip_${index}`}>{`${getLabel(
            `lotColor_${o?.toUpperCase()}`
          )}: ${obj?.[o]}`}</li>
        ))}
      </ul>
    );
  };

  const renderNavMenu = (currentTab) => {
    const isWinemaking = currentTab?.startsWith('winemaking');
    const wineMakingType = isWinemaking ? currentTab.split('-')[1]?.toUpperCase() : null;
    const isPhysical = currentTab === 'physical';
    const isAnalysis = currentTab === 'analysis';
    const isTreatment = currentTab === 'treatment';
    const isAppTemplates = currentTab === 'app-templates';

    let count = 0;
    let createRoute = '';
    let createLabel = '';

    if (isAppTemplates) {
      count = appTemplates?.length || 0;
      createRoute = `/protocols/app-templates/new`;
      createLabel = getLabel("appTemplateNew");
    } else if (isWinemaking) {
      count = (winemakingProtocols?.filter(w => w?.protocol_type === wineMakingType) || [])?.length;
      createRoute = `/protocols/winemaking/${wineMakingType?.toLowerCase()}/new`;
      if (wineMakingType === 'PRE') createLabel = getLabel("createWineMakingProtocol");
      else if (wineMakingType === 'POST') createLabel = getLabel("createWineMakingPostProtocol");
      else if (wineMakingType === 'SPARKLING') createLabel = getLabel("createWineMakingSparklingProtocol");
      else if (wineMakingType === 'CIP') createLabel = getLabel("createWineMakingCIPProtocol");
    } else if (isPhysical) {
      count = (winemakingProtocols?.filter(w => w?.protocol_type === 'PHYSICAL') || [])?.length;
      createRoute = `/protocols/winemaking/physical/new`;
      createLabel = getLabel("createWineMakingPhysicalProtocol");
    } else {
      count = protocolsLen?.tot || 0;
      createRoute = `/protocols/${currentTab}/new`;
      createLabel = isTreatment ? getLabel("createTreatmentProtocol") : getLabel("createAnalysisProtocol");
    }

    return (
      <div className="primary_container_menu">
        <div>
          <h5 className="tanks_title">
            {getLabel("numTotProtocols")}
            <span className="tanks_span">{count}</span>
          </h5>
        </div>
        <div
          className={isAnalysis ? "tanks_wrapper" : "protocols_wrapper"}
        >
          {role === ROLES.CLIENT && (
            <NavLink
              className="cta_wrapper"
              to={createRoute}
            >
              <CTA customClassName={"create"}>
                {createLabel}{" "}
                <Icon icon="fa-plus" />
              </CTA>
            </NavLink>
          )}
          {isAnalysis && (
            <div className="tanks_recap">
              <div className="tanks_recap_wrapper">
                <InfoCard
                  label={getLabel("numTotGrapeLots")}
                  number={protocolsLen?.grape}
                  img={isMobile || true ? grape : null}
                  isGrey={protocolsLen?.grape === 0}
                  content={getTooltipContent(protocolsLen?.grapes)}
                />
                <InfoCard
                  label={getLabel("numTotMustLots")}
                  number={protocolsLen?.must}
                  img={isMobile || true ? must : null}
                  isGrey={protocolsLen?.musts === 0}
                  content={getTooltipContent(protocolsLen?.musts)}
                />
                <InfoCard
                  label={getLabel("numTotWineLots")}
                  number={protocolsLen?.wine}
                  img={isMobile || true ? wine : null}
                  isGrey={protocolsLen?.wines === 0}
                  content={getTooltipContent(protocolsLen?.wines)}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const getCurrentProtocols = () => {
    if (activeTab === 'analysis') {
      return analysisProtocols || [];
    } else if (activeTab === 'treatment') {
      return treatmentProtocols || [];
    } else if (activeTab === 'physical') {
      return winemakingProtocols?.filter(p => p?.protocol_type === 'PHYSICAL') || [];
    } else if (activeTab === 'winemaking-pre') {
      return winemakingProtocols?.filter(p => p?.protocol_type === 'PRE') || [];
    } else if (activeTab === 'winemaking-post') {
      return winemakingProtocols?.filter(p => p?.protocol_type === 'POST') || [];
    } else if (activeTab === 'winemaking-sparkling') {
      return winemakingProtocols?.filter(p => p?.protocol_type === 'SPARKLING') || [];
    } else if (activeTab === 'winemaking-cip') {
      return winemakingProtocols?.filter(p => p?.protocol_type === 'CIP') || [];
    } else if (activeTab === 'app-templates') {
      return appTemplates || [];
    }
    return [];
  };

  const getCurrentType = () => {
    if (activeTab === 'analysis') return 'analysis';
    if (activeTab === 'treatment') return 'treatment';
    if (activeTab === 'physical') return 'PHYSICAL';
    if (activeTab === 'winemaking-pre') return 'PRE';
    if (activeTab === 'winemaking-post') return 'POST';
    if (activeTab === 'winemaking-sparkling') return 'SPARKLING';
    if (activeTab === 'winemaking-cip') return 'CIP';
    if (activeTab === 'app-templates') return 'app-templates';
    return 'analysis';
  };

  const isWinemakingTab = () => {
    return activeTab === 'physical' || activeTab?.startsWith('winemaking');
  };

  const isAppTemplatesTab = () => activeTab === 'app-templates';

  return (
    <div className="primary_container">
      <SecondaryBar
        breadCrumb={[getLabel("protocols")]}
        isBasePathNeeded={false}
      />
      {renderHeader()}
      <TabSelector
        tabs={tabs}
        onTabChange={handleTabChange}
        activeTab={activeTab}
      />
      {renderNavMenu(activeTab)}
      <Suspense
        fallback={
          <SkeletonTableEdit colCount={activeTab === "analysis" ? 5 : isWinemakingTab() ? 6 : 4} />
        }
      >
        {isAppTemplatesTab() ? (
          <ApplicationTemplateList
            templates={getCurrentProtocols()}
            users={users}
            configs={configs}
          />
        ) : isWinemakingTab() ? (
          <WineMakingProtocolList
            type={getCurrentType()}
            protocols={getCurrentProtocols()}
            users={users}
            configs={configs}
            isMobile={isMobile}
          />
        ) : (
          <ProtocolList
            type={getCurrentType()}
            protocols={getCurrentProtocols()}
            users={users}
            isMobile={isMobile}
          />
        )}
      </Suspense>
    </div>
  );
};

export default Protocols;
